/**
 * PLAT-9968 — Canvas-side controller for the composition floating action
 * menu + quick-edit popover.
 *
 * Lives alongside the Creatomate clip-preview canvas. Subscribes to the
 * adapter's `activeElementIds` (populated by `renderer.onActiveElementsChange`
 * — i.e. clicks inside the iframe). When the active element is an intro
 * card, end card, or namecard with at least one `allowQuickEdit` text
 * control, renders the Canva-style action menu anchored over the renderer
 * container. Edit opens the same `CompositionQuickEditPopover` the
 * transcript-chip path mounts (PLAT-9968 transcript surface).
 *
 * Delete routes through `editor.commandRemoveElement` (the command path —
 * marks dirty, syncs the renderer, registers undo) and clears the
 * adapter's active selection. Duplicate is intentionally
 * un-wired in v1 — see PLAT-9968 follow-ups; the prop on
 * `CompositionCanvasActionMenu` is left unset, which hides the button.
 *
 * Positioning: projects the selected element's source `x/y/width/height`
 * into viewport pixels via `computeElementViewportBox` and anchors the
 * menu just above the projected box's top edge (flipping below when the
 * element hugs the canvas top), clamped to the canvas rect.
 * Polls at 10Hz while the menu is visible so the toolbar tracks
 * composition animations during playback / drag — the Creatomate
 * integration doesn't expose element-rect change events.
 */
import { trackCompositionTextEdited } from '@src/analytics/EventTracker';
import { useCompositionQuickEditOpenedTracking } from '@src/analytics/hooks/useCompositionQuickEditOpenedTracking';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { buildAITextPopoverContext } from '@src/components/sidepanel/CompositionSettings/controls/aiTextPopoverContext';
import { useAITextGeneration } from '@src/components/sidepanel/CompositionSettings/controls/useAITextGeneration';
import { CompositionQuickEditPopover } from '@src/components/transcript/CompositionQuickEditPopover';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import {
  useTranscription,
  useTranslationStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { isOriginalLang } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { removeOverlayAndPersist } from '@src/utility/overlayDeletion';
import { buildOverlayMarker } from '@src/utility/overlayMarker';
import { computeElementViewportBox } from '@src/videoTranscriptionProcessor/translation/compositionCanvasGeometry';
import { findNodeInTree } from '@src/videoTranscriptionProcessor/translation/compositionElementPath';
import {
  computeMarkerQuickEditFields,
  resolveQuickEditTargetId,
} from '@src/videoTranscriptionProcessor/translation/computeMarkerQuickEditFields';
import { extractTranslatableElements } from '@src/videoTranscriptionProcessor/translation/extractTranslatableElements';
import { getTranslatableSourceElements } from '@src/videoTranscriptionProcessor/translation/getTranslatableSourceElements';
import { readActiveCaptionLanguage } from '@src/videoTranscriptionProcessor/translation/readActiveCaptionLanguage';
import { resolveCanvasTarget } from '@src/videoTranscriptionProcessor/translation/resolveCanvasTarget';
import { routeRetranslateTargetId } from '@src/videoTranscriptionProcessor/translation/routeRetranslateTargetId';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { CompositionCanvasActionMenu } from './CompositionCanvasActionMenu';
import { useCompositionPinDispatchers } from './hooks/useCompositionPinDispatchers';

import type { CompositionQuickEditSurface } from '@src/analytics/EventTracker';
import type { RetranslateCompositionDispatch } from '@src/components/transcript/CompositionQuickEditPopover';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { TranscriptElement } from '@src/types.ts/transcript';
import type { EditorStoreLike } from '@src/utility/compositionSettings';
import type { OverlayMarkerAnchorInput } from '@src/utility/overlayMarker';
import type { ViewportBox } from '@src/videoTranscriptionProcessor/translation/compositionCanvasGeometry';
import type { CanvasTarget } from '@src/videoTranscriptionProcessor/translation/resolveCanvasTarget';

const logger = LogManager.getLogger('component:CompositionCanvasActionsLayer');

/**
 * PLAT-10644 — empty selections arriving within this window after a select
 * are treated as Creatomate's echo-deselect (fired immediately after most
 * successful selects, over postMessage) and ignored; later ones are genuine
 * canvas-background clicks and dismiss the menu + popover.
 */
const ECHO_DESELECT_WINDOW_MS = 400;

/**
 * PLAT-10211 — minimal host surface the canvas action layer reads. Both the
 * clip-preview `ClipVideoCreatorAdapter` and the global `VideoCreatorStore`
 * satisfy it structurally, so the same layer drives the canvas menu in the
 * Content Studio clip preview AND the Creator Studio stage. `setActiveElements`
 * clears/sets the renderer selection so the menu drops out on dismiss.
 */
export interface CompositionCanvasHost {
  activeElementIds: string[];
  currentVideo?: {
    extraElementData?: Record<string, unknown> | null;
    /** PLAT-10915 — the clip, carried into the generation request context. */
    id?: string;
  } | null;
  storyId?: string | null;
  /**
   * The showcase (workspace) the surface is operating in. Serves two callers on
   * the same object, so it is declared once: PLAT-10915 layers this workspace's
   * Brand Kit into generation (E3/E10), and PLAT-10875 forwards it on
   * retranslate so the backend loads this workspace's translation memory. Both
   * hosts expose it; optional throughout so a host that doesn't falls back to
   * the backend's own showcase resolution, and `id` is optional because the
   * Brand Kit caller does not guarantee it.
   */
  organization?: { id?: string } | null;
  setActiveElements: (...elementIds: string[]) => unknown;
  /**
   * Predicate identifying the primary (original) story video element. Used to
   * suppress the action menu when the selection is the primary video — there's
   * nothing to quick-edit and it isn't a deletable overlay. Both
   * `VideoCreatorStore` and `ClipVideoCreatorAdapter` implement it; optional so
   * a host that doesn't simply keeps the prior (always-show) behavior.
   */
  isOriginalVideoElement?: (elementSource: unknown) => boolean;
  /**
   * PLAT-11105 — is the surface's player running? Playback tears the editing
   * chrome down (see the play-transition effect below). Both hosts expose it as
   * an observable (`VideoCreatorStore.isPlaying` /
   * `ClipVideoCreatorAdapter.isPlaying`); optional so a host that doesn't keeps
   * the prior always-visible behavior.
   */
  isPlaying?: boolean;
}

/**
 * Editor surface the layer needs: popover-compatible (`EditorStoreLike`)
 * is sufficient — `editor` is `IVideoEditor`, which already exposes the
 * command API (`commandRemoveElement` / `commandUpdateElement`) the menu
 * and popover route through. Element removal goes through the command
 * path so it marks dirty, syncs the renderer, and registers undo.
 */
export type CompositionCanvasEditorLike = EditorStoreLike & {
  /**
   * PLAT-10913 — the surface's transcript, used by the AI text control to
   * resolve what a graphic reads. Present on the clip-preview store, because a
   * clip IS a window on the story transcript; absent on the Creator Studio
   * store, where the story transcript lives in `TranscriptionStore` and is
   * passed in via the `transcriptElements` prop instead (PLAT-10935 DS1).
   */
  transcriptElements?: ReadonlyArray<TranscriptElement>;
};

/**
 * PLAT-12102 — what a contextual bar's Edit calls: opens the quick-edit
 * popover for the selected plain text (whose chip the bar replaced), anchored
 * to the bar's button. A second call while it is open closes it, as the chip's
 * pencil does.
 */
export interface CompositionCanvasQuickEditHandle {
  openQuickEdit(anchor: HTMLElement): void;
}

interface Props {
  /**
   * Ref to the Creatomate renderer container — used to compute the
   * top-center anchor coordinates each frame the menu is visible.
   */
  rendererContainerRef: React.RefObject<HTMLElement>;
  adapter: CompositionCanvasHost | null;
  editorStore: CompositionCanvasEditorLike | null;
  videoSourceStore: VideoSourceStore | null;
  /**
   * PLAT-10935 DS1/DS2 — the surface's transcript for the AI text control,
   * taking precedence over `editorStore.transcriptElements`. Creator Studio
   * supplies it from `TranscriptionStore`; clip preview omits it and keeps
   * reading the clip-local transcript off its editor store.
   */
  transcriptElements?: ReadonlyArray<TranscriptElement>;
  /**
   * Optional story id — forwarded to analytics events for funnel slicing.
   * Read off the adapter when omitted.
   */
  storyId?: string | null;
  /**
   * Surface tag for analytics + the popover. Defaults to the clip-preview
   * surface so existing callers are unchanged; the Creator Studio stage
   * passes `'creator-studio-canvas'` (PLAT-10211).
   */
  surface?: CompositionQuickEditSurface;
  /**
   * PLAT-10191 — optional post-mutation persistence hook. After a delete the
   * editor state must be flushed to storage or the change is lost on reload
   * (the command's own autosave snapshots the PRE-command state — see
   * `handleDelete`). Creator Studio wires this to `saveStoryAndVideo`. When
   * omitted (e.g. surfaces that persist through another path), the layer only
   * mutates the editor + cleans metadata.
   */
  onPersistAfterMutation?: () => void;
  /**
   * PLAT-12102 DS5 — the contextual toolbars are mounted over this canvas, so
   * the chip stays away from the elements they cover (plain text, photos,
   * logos, b-roll): one selection, one control. Both hosts pass it.
   */
  barsMounted?: boolean;
  /**
   * PLAT-12102 — set by a surface whose text bar has Edit (the clip preview,
   * which has no panel to edit a text's content in): the bar opens this
   * layer's quick-edit popover through it. A plain text then still resolves
   * a target, for the popover; only its chip stays hidden (`barsMounted`).
   */
  quickEditHandleRef?: React.Ref<CompositionCanvasQuickEditHandle>;
}

export const CompositionCanvasActionsLayer: React.FC<Props> = observer(
  ({
    rendererContainerRef,
    adapter,
    editorStore,
    videoSourceStore,
    transcriptElements: transcriptElementsProp,
    storyId: storyIdProp,
    surface = 'content-studio-clip-preview',
    onPersistAfterMutation,
    barsMounted = false,
    quickEditHandleRef,
  }) => {
    const activeId = adapter?.activeElementIds[0];
    const extraElementData = adapter?.currentVideo?.extraElementData ?? null;
    const storyId = storyIdProp ?? adapter?.storyId ?? undefined;
    const translationStore = useTranslationStore();
    const transcriptionStore = useTranscription();
    const userIdentity = useUserIdentity();

    // PLAT-10046 — active caption language for the popover. Read off the
    // same editor the surface mutates via the captions toggle; tracked in
    // a MobX-observed reaction by re-reading on every render (the layer is
    // already an observer; clip-preview's editor karaoke config is the
    // observable source the captions picker mutates). When no editor is
    // bound yet (early-mount), fall back to `'original'` so the popover
    // would render the PLAT-9968 single-input shape — but in practice the
    // popover only mounts once `editorStore` is non-null below.
    const activeLanguage = useMemo(
      () =>
        editorStore
          ? readActiveCaptionLanguage(editorStore.editor)
          : 'original',
      // Second dep is a call, not a value the linter can track — deliberate, so
      // the memo re-runs when the karaoke language changes. (Was an
      // `eslint-disable` for `react-hooks/exhaustive-deps`, a rule this repo's
      // config doesn't register, which errors as an unknown rule once linted.)
      [editorStore, editorStore?.editor.getDefaultKaraokeConfig?.()?.language],
    );

    // PLAT-10046 — per-language composition-phrase lookup. The Map
    // identity comes from `getCompositionPhrasesByNormalizedSource`,
    // which memoizes per `TranslationData` reference — so we MUST include
    // the live `translations.get(lang)` reference in the dep list.
    // Without it, the canvas-layer re-renders after a cache swap but
    // `useMemo` returns the stale Map (deps unchanged), and reopening
    // the popover shows pre-save values.
    const activeLangTranslationData =
      translationStore.translations.get(activeLanguage) ?? null;
    const compositionPhrases = useMemo(
      () =>
        translationStore.getCompositionPhrasesByNormalizedSource(
          activeLanguage,
        ),
      [translationStore, activeLanguage, activeLangTranslationData],
    );

    // Resolve the active element to:
    //   1. the containing composition placement (if the click landed
    //      inside a composition, even on a leaf text node), or
    //   2. any text-bearing element (top-level overlay OR a nested leaf
    //      we can edit standalone — covers Path B compositions where the
    //      iframe targets the inner text node directly), or
    //   3. a generic fallback that lets the user at least delete the
    //      selection — keeps the menu visible whenever the renderer
    //      reports an active id, which makes "menu missing" reports
    //      unambiguously about the selection wiring, not our resolution.
    //
    // NOTE: this is the *current-frame* target; below we latch the last
    // non-null value into `latchedTarget` so an echo deselect from the
    // iframe doesn't blink the menu away.
    // Is the current selection the primary (original) story video? It has no
    // quick-edit fields and isn't a deletable overlay, so the action pill
    // (Edit / Delete / X) is meaningless there. Match by the same predicate the
    // rest of the app uses (URL identity), not element shape, so split scene
    // clips of the base footage are all covered.
    const activeIsPrimaryVideo = useMemo(() => {
      if (!activeId || !videoSourceStore || !adapter?.isOriginalVideoElement)
        return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? findNodeInTree(topLevel, activeId)
        : null;
      return !!activeSource && adapter.isOriginalVideoElement(activeSource);
    }, [activeId, videoSourceStore?.source, adapter]);

    // PLAT-10797 — is the current selection a caption (karaoke-track
    // element)? Captions are transcript-driven karaoke elements, not
    // standalone text overlays: their text is edited through the transcript
    // and deleting a single caption element isn't a supported flow, so the
    // action pill (Edit / Delete / X) is suppressed for them — same
    // treatment as the primary video above. `track` arrives as a number
    // from our own source but as a string from Creatomate state (see
    // `KaraokeProducer`), hence the `Number(...)` normalization.
    const activeIsCaption = useMemo(() => {
      if (!activeId || !videoSourceStore) return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? findNodeInTree(topLevel, activeId)
        : null;
      return (
        Number((activeSource as { track?: unknown } | null)?.track) ===
        KARAOKE_TRACK_NUMBER
      );
    }, [activeId, videoSourceStore?.source]);

    // PLAT-10797 — on the Creator Studio stage, also suppress the pill for
    // standalone plain-text elements (the kind the Text panel's Font Style
    // presets / "Add New Text Element" create). Selecting one there opens
    // the Text settings sidebar, which owns editing — the pill is redundant.
    // The clip preview keeps the pill unless the text bar is mounted there
    // (PLAT-12102): without it, the pill's quick-edit popover is the only way
    // to edit a text overlay in that modal; with it, the bar's Edit opens the
    // same popover through `quickEditHandleRef`, so the text keeps a target
    // (see `pillSuppressed`). Top-level membership (not `findNodeInTree`) is deliberate —
    // text leaves nested in compositions/groups must keep resolving to
    // their composition target.
    const activeIsPlainText = useMemo(() => {
      if (surface !== 'creator-studio-canvas' && !barsMounted) return false;
      if (!activeId || !videoSourceStore) return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? topLevel.find(el => (el as { id?: unknown }).id === activeId)
        : null;
      return (activeSource as { type?: unknown } | null)?.type === 'text';
    }, [surface, barsMounted, activeId, videoSourceStore?.source]);
    // A plain text with nothing to open the popover for it has no target at
    // all; one a bar can edit keeps its target and loses only the pill.
    const activeIsPlainTextWithoutEdit =
      activeIsPlainText && !quickEditHandleRef;

    // Audio elements (music, dub voice) have no picture on the canvas and no
    // quick-edit fields, so the pill is meaningless there — same treatment as
    // captions above, on both surfaces. Top-level only, so an audio leaf inside
    // a composition still resolves to its composition target.
    const activeIsAudio = useMemo(() => {
      if (!activeId || !videoSourceStore) return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? topLevel.find(el => (el as { id?: unknown }).id === activeId)
        : null;
      return (activeSource as { type?: unknown } | null)?.type === 'audio';
    }, [activeId, videoSourceStore?.source]);

    // PLAT-12098 (PLAT-12097 DE3) — where the bars are mounted (Creator
    // Studio; the clip preview since PLAT-12102), a top-level photo, logo or
    // b-roll clip has its own contextual bar, with its own
    // Delete and More. A chip beside it was a second control for the same
    // selection, and could sit over the bar. One selection, one control: the
    // bar wins, the chip stays for graphics cards and shapes. The primary
    // video is excluded above (`activeIsPrimaryVideo`), so `video` here is
    // b-roll.
    const activeHasBar = useMemo(() => {
      if (!barsMounted) return false;
      if (!activeId || !videoSourceStore) return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? topLevel.find(el => (el as { id?: unknown }).id === activeId)
        : null;
      const type = (activeSource as { type?: unknown } | null)?.type;
      return type === 'image' || type === 'video';
    }, [barsMounted, activeId, videoSourceStore?.source]);

    const resolvedTarget = useMemo<CanvasTarget | null>(() => {
      // Suppress the menu on the primary video (see `activeIsPrimaryVideo`),
      // on captions, on audio, on Creator Studio plain text (see
      // `activeIsCaption` / `activeIsAudio` / `activeIsPlainTextWithoutEdit`,
      // PLAT-10797), and where a contextual bar shows instead (`activeHasBar`,
      // PLAT-12098).
      if (
        !activeId ||
        !videoSourceStore ||
        activeIsPrimaryVideo ||
        activeIsCaption ||
        activeIsAudio ||
        activeIsPlainTextWithoutEdit ||
        activeHasBar
      )
        return null;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      return resolveCanvasTarget({
        activeId,
        videoSourceElements: topLevel,
        extraElementData,
      });
      // `videoSourceStore?.source` (not `videoSourceStore`) is the
      // observable.ref MobX tracks; depending on the store reference would
      // cache the resolved target across `commandUpdateElement` syncs.
    }, [
      activeId,
      videoSourceStore?.source,
      extraElementData,
      activeIsPrimaryVideo,
      activeIsCaption,
      activeIsAudio,
      activeIsPlainTextWithoutEdit,
      activeHasBar,
    ]);

    // Latch the last non-null target. Creatomate's iframe echoes a
    // deselect (`onActiveElementsChange([])`) immediately after a
    // successful click in some scenarios — without latching, the menu
    // would flash and disappear within one render. The latched target
    // is cleared on explicit user dismissal (X / Escape / Delete) or
    // a new selection landing on a different element.
    const [latchedTarget, setLatchedTarget] = useState<CanvasTarget | null>(
      null,
    );
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(
      null,
    );
    // PLAT-11090 — the selected graphic's projected box. Kept in a ref because
    // the 10Hz recompute below must not re-render, and snapshotted into state
    // when the popover opens: feeding a fresh box into placement every tick
    // would make the popover walk around mid-edit.
    const elementBoxRef = useRef<ViewportBox | null>(null);
    const [popoverClearRect, setPopoverClearRect] =
      useState<ViewportBox | null>(null);
    // Re-arm on a new selection only. `resolvedTarget` is re-derived on every
    // video-source replacement, so keying off the object would re-open the pill
    // after a dismissal that leaves the selection in place — which the playback
    // teardown below deliberately does (PLAT-11402).
    const latchedTargetIdRef = useRef<string | null>(null);
    useEffect(() => {
      if (!resolvedTarget) {
        // The latch itself is intentionally NOT cleared here — Creatomate's
        // iframe echoes a deselect after most successful selects, with no
        // follow-up event, so the menu would flash and vanish within one
        // render. It persists until an explicit dismissal (X / Escape /
        // Delete) or a new selection. Only the id is forgotten, so re-selecting
        // the same element re-arms.
        latchedTargetIdRef.current = null;
        return;
      }
      if (resolvedTarget.id === latchedTargetIdRef.current) return;
      latchedTargetIdRef.current = resolvedTarget.id;
      setLatchedTarget(resolvedTarget);
    }, [resolvedTarget]);

    // Selecting the primary video, a caption, audio, Creator Studio plain
    // text, or an element with its own bar IS a deliberate new selection (not an echo deselect), so it must
    // drop any menu left over from a prior overlay / composition.
    // `resolvedTarget` is null in these cases, and the latch intentionally
    // ignores null — so clear it explicitly here.
    useEffect(() => {
      if (
        activeIsPrimaryVideo ||
        activeIsCaption ||
        activeIsAudio ||
        activeIsPlainTextWithoutEdit ||
        activeHasBar
      ) {
        setLatchedTarget(null);
        setPopoverAnchor(null);
      }
    }, [
      activeIsPrimaryVideo,
      activeIsCaption,
      activeIsAudio,
      activeIsPlainTextWithoutEdit,
      activeHasBar,
    ]);

    // PLAT-10644 — dismiss on a genuine canvas-background deselect. Clicks
    // INSIDE the Creatomate iframe never reach the parent document, so the
    // outside-mousedown handler below can't see them; the only signal is
    // the iframe's own deselect event. The latch must still ignore the
    // echo-deselect Creatomate fires immediately after most successful
    // selects — the only difference between echo and background click is
    // timing, so empty selections inside a short post-select window are
    // treated as echoes. Both adapters assign a FRESH `activeElementIds`
    // array per renderer event, so the array reference works as a
    // per-event dependency even for repeated `[]` → `[]` deselects.
    const activeElementIds = adapter?.activeElementIds;
    const lastSelectAtRef = React.useRef(0);
    useEffect(() => {
      if (activeId) lastSelectAtRef.current = Date.now();
    }, [activeId]);
    useEffect(() => {
      if (!activeElementIds || activeElementIds.length > 0) return;
      if (Date.now() - lastSelectAtRef.current < ECHO_DESELECT_WINDOW_MS)
        return;
      setLatchedTarget(null);
      setPopoverAnchor(null);
    }, [activeElementIds]);

    // PLAT-11105 — pressing play is an intent to WATCH, not to edit: the pill
    // goes (it would otherwise chase the animating graphic around the frame via
    // the 10Hz tracker below) and the quick-edit popover closes, discarding an
    // unsaved draft exactly as Cancel does.
    // The selection is left alone — it is app-wide state, and clearing it
    // emptied every surface bound to the selected element (PLAT-11402).
    // Keys off the transitions, not the playing state: a graphic selected
    // mid-playback still opens its pill, and pause puts back a pill that
    // playback hid, since the pre-play selection is still there. Only one that
    // playback hid — Escape dismisses the pill WITHOUT deselecting, and that
    // dismissal has to survive a play/pause. The popover never comes back.
    const isPlaying = !!adapter?.isPlaying;
    const wasPlayingRef = useRef(isPlaying);
    const pillHiddenByPlaybackRef = useRef(false);
    useEffect(() => {
      const justStarted = isPlaying && !wasPlayingRef.current;
      const justStopped = !isPlaying && wasPlayingRef.current;
      wasPlayingRef.current = isPlaying;
      if (justStarted) {
        pillHiddenByPlaybackRef.current = !!latchedTarget;
        setLatchedTarget(null);
        setPopoverAnchor(null);
        return;
      }
      if (justStopped) {
        const restore = pillHiddenByPlaybackRef.current && resolvedTarget;
        pillHiddenByPlaybackRef.current = false;
        if (restore) setLatchedTarget(resolvedTarget);
      }
    }, [isPlaying, resolvedTarget, latchedTarget]);

    const activeTarget = latchedTarget;

    // PLAT-12102 — a plain text whose bar has Edit keeps its target for the
    // popover but shows no pill: the bar is its one control. Read off the
    // latched target, not `activeIsPlainText`, so the iframe's echo deselect
    // cannot flash the pill in. Only where the bars are mounted — a clip
    // preview without them keeps the pill for text.
    const pillSuppressed = useMemo(() => {
      if (!activeTarget || !quickEditHandleRef || !videoSourceStore)
        return false;
      if (surface !== 'creator-studio-canvas' && !barsMounted) return false;
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      const activeSource = Array.isArray(topLevel)
        ? topLevel.find(el => (el as { id?: unknown }).id === activeTarget.id)
        : null;
      return (activeSource as { type?: unknown } | null)?.type === 'text';
    }, [
      activeTarget,
      quickEditHandleRef,
      surface,
      barsMounted,
      videoSourceStore?.source,
    ]);

    // Dismissal: the X button on the menu pill, the popover's Cancel /
    // X / Save, the Escape key, selecting a different composition, OR a
    // mousedown anywhere outside the menu + popover (PLAT-10644 — the
    // user expects clicking away to close both the popover AND the pill).
    // Clicks physically inside the Creatomate iframe never reach the
    // parent document's listeners, so canvas clicks stay delegated to the
    // iframe's selection logic by construction — no wrapper exemption
    // needed. Clicks on the letterbox AROUND the canvas (still inside the
    // renderer container) DO reach the document and now dismiss, which is
    // what the old wrapper exemption wrongly swallowed.

    // Escape dismisses.
    useEffect(() => {
      if (!activeTarget) return;
      const handleKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          // Opened from a bar, only the popover goes: the text stays selected
          // with its bar, and its Edit can open the popover again.
          if (pillSuppressed) {
            setPopoverAnchor(null);
            return;
          }
          setLatchedTarget(null);
          setPopoverAnchor(null);
        }
      };
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }, [activeTarget, pillSuppressed]);

    // Outside-click dismissal: any mousedown outside the action menu pill
    // and the quick-edit popover dismisses both, and clears the renderer's
    // selection (same as the pill's X) so the iframe drops its selection
    // chrome too.
    // A text whose bar has Edit (`pillSuppressed`) has no pill to dismiss, and
    // clicking away from it is the bar's and the surface's call (the clip
    // preview's own outside-click deselect, which exempts the bar); the popover
    // closes itself on an outside mousedown.
    useEffect(() => {
      if (!activeTarget || pillSuppressed) return;
      const handleMouseDown = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (target instanceof Element) {
          if (target.closest('[data-testid="composition-canvas-action-menu"]'))
            return;
          if (target.closest('[data-testid="composition-quick-edit-popover"]'))
            return;
        }
        // In the Creator Studio stage the selected element drives a persistent
        // settings side panel (RightSidePanel) plus the floating text toolbar.
        // A mousedown on that editing chrome is NOT "clicking away" — clearing
        // the selection there collapses the Graphics → Text panel back to its
        // presets list mid-edit. Only a click that lands on the canvas /
        // letterbox (inside the renderer container) is a genuine dismiss +
        // deselect on this surface. The clip-preview surfaces (a modal with no
        // persistent element panel) keep the original click-anywhere-outside
        // behavior.
        if (
          surface === 'creator-studio-canvas' &&
          !rendererContainerRef.current?.contains(target)
        ) {
          return;
        }
        setLatchedTarget(null);
        setPopoverAnchor(null);
        if (adapter && adapter.activeElementIds.length > 0) {
          void adapter.setActiveElements();
        }
      };
      document.addEventListener('mousedown', handleMouseDown, true);
      return () =>
        document.removeEventListener('mousedown', handleMouseDown, true);
    }, [activeTarget, pillSuppressed, adapter, surface, rendererContainerRef]);

    const popoverFields = useMemo(() => {
      if (!activeTarget || !videoSourceStore) return [];
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      // Delegate to the SAME shared resolver the transcript surfaces use
      // (`computeMarkerQuickEditFields`) so the canvas Edit affordance is
      // gated on identical "has editable fields" logic. The canvas
      // previously had a bespoke, narrower resolution — composition
      // `allowQuickEdit` controls OR a top-level text node whose id exactly
      // matched the active id — which missed the transcript's
      // composition→text-leaf + time-overlap fallbacks. The symptom: a
      // graphic overlay whose editable text lives on a nested leaf (or a
      // composition with no `allowQuickEdit` controls) showed the pill with
      // Edit DISABLED on the canvas while the transcript chip enabled it for
      // the same element (PLAT-10122 follow-up). The marker's time range is
      // read from the editor timeline element — numeric `time` / `end_time`,
      // the same source `useTimelineMarkers` feeds the transcript path.
      const timelineEl = editorStore?.editor.timeline.getElement(
        activeTarget.id,
      );
      // `time` / `endTime` are read here but NOT in the dep array (only
      // `editorStore` the stable store ref is). They feed solely the (c)
      // time-overlap fallback inside `computeMarkerQuickEditFields`; a pure
      // time/duration change without a `videoSourceStore.source` swap won't
      // re-run this memo. Acceptable: the (c) path is a rare last resort and
      // the marker's range is stable for the lifetime of a selection.
      const time = typeof timelineEl?.time === 'number' ? timelineEl.time : 0;
      const endTime =
        typeof timelineEl?.end_time === 'number' ? timelineEl.end_time : time;
      return computeMarkerQuickEditFields(topLevel, extraElementData, {
        id: activeTarget.id,
        time,
        endTime,
      });
      // Depend on `videoSourceStore?.source` (the observable.ref) so the
      // popover's fields refresh after `commandUpdateElement` → the
      // editor observer fires `refreshTimeline()` → `setSource(...)`.
      // Without this, reopening the popover after a save would show the
      // pre-save value cached by useMemo (PLAT-9968 regression — the
      // canvas would render the new text but the input would show the
      // old text until a full reload re-read the source).
    }, [activeTarget, videoSourceStore?.source, extraElementData, editorStore]);

    // PLAT-10913 — base-text context for an `aiText` row, so a graphic reads
    // the same words whether quick edit was opened from the transcript or the
    // canvas. The transcript surfaces get their marker from
    // `useTimelineMarkers`; the canvas has no marker list, so build the same
    // shape from the timeline element's range plus the placement's stored
    // `transcriptAnchor` (absent → the time-window mapping inside
    // `findOverlayRanges` takes over, exactly as it does for the underline).
    // The transcript comes from the prop when the surface supplies one
    // (Creator Studio, PLAT-10935 DS2) and from the editor store otherwise
    // (clip preview, whose store carries the clip-local transcript).
    // `undefined` when neither has one, which leaves the row as a plain
    // editable text row.
    const aiTextContext = useMemo(() => {
      if (!activeTarget || !editorStore) return undefined;
      const transcriptElements =
        transcriptElementsProp ?? editorStore.transcriptElements;
      if (!transcriptElements) return undefined;
      const timelineEl = editorStore.editor.timeline.getElement(
        activeTarget.id,
      );
      const meta = extraElementData?.[activeTarget.id] as
        | {
            transcriptAnchor?: OverlayMarkerAnchorInput;
            placement?: string | null;
          }
        | undefined;
      const marker = buildOverlayMarker({
        id: activeTarget.id,
        time: timelineEl?.time,
        endTime: timelineEl?.end_time,
        anchor: meta?.transcriptAnchor,
        // DS14 — the clip-boundary pair covers more than its anchored passage.
        placement: meta?.placement,
      });
      return { transcriptElements, overlay: marker };
    }, [activeTarget, editorStore, extraElementData, transcriptElementsProp]);

    // PLAT-10915 — the canvas Edit pill opens the SAME popover as the
    // transcript chip, so it wires the same generation. Its own instance (the
    // two surfaces are separate component trees), which means an insert-time
    // failure recorded on the transcript side isn't visible here — the retry
    // is, and that is what matters.
    const aiTextGenerationContext = useMemo(
      () => ({
        storyId,
        showcaseId: adapter?.organization?.id,
        // PLAT-10935 DS3 — Creator Studio edits the STORY video, so its
        // `currentVideo.id` is not a clip id. Sending it as `clipId` would
        // point the backend's Brand Kit resolution (E3) at a clip that does
        // not exist; the context is by reference, so the right move is to omit
        // what this surface genuinely doesn't have.
        ...(surface === 'creator-studio-canvas'
          ? {}
          : { clipId: adapter?.currentVideo?.id }),
      }),
      [storyId, adapter?.organization?.id, adapter?.currentVideo?.id, surface],
    );
    const getCurrentVideoForAIText = useCallback(
      () => adapter?.currentVideo ?? null,
      [adapter],
    );
    const refreshSourceForAIText = useCallback(
      () => videoSourceStore?.refreshSource(),
      [videoSourceStore],
    );
    const aiTextGeneration = useAITextGeneration({
      // The layer is mounted by BOTH studios, so its own surface prop is the
      // answer — the clip preview and the Creator Studio stage share this code.
      surface,
      editorStore,
      getCurrentVideo: getCurrentVideoForAIText,
      refreshSource: refreshSourceForAIText,
      context: aiTextGenerationContext,
      transcriptElements: aiTextContext?.transcriptElements,
    });

    // Shared with the transcript chip's popover — the two entry points must
    // behave identically, and two hand-rolled copies is how that stops being
    // true.
    const aiTextPopoverContext = useMemo(
      () =>
        aiTextContext
          ? buildAITextPopoverContext({ aiTextGeneration, ...aiTextContext })
          : undefined,
      [aiTextContext, aiTextGeneration],
    );

    const [menuRect, setMenuRect] = useState<{
      left: number;
      top: number;
    } | null>(null);

    // Recompute the menu's anchor coordinates whenever the active
    // composition changes, the renderer container resizes, or the user
    // scrolls the surrounding modal. ResizeObserver covers the first two;
    // a window scroll listener covers the third; a 10Hz interval tracks
    // playback / drag animations (Creatomate doesn't surface element-rect
    // change events).
    useEffect(() => {
      if (!activeTarget) {
        setMenuRect(null);
        return;
      }
      const container = rendererContainerRef.current;
      if (!container) return;

      // --- Menu geometry tunables (all in one place) ---
      // Half the pill's rendered height (30px icon button + 4px padding +
      // 1px border, top and bottom). The pill self-centers via
      // `transform: translate(-50%, -50%)` on `MenuPosition`, so this is
      // what converts "pill edge" targets into the CENTER anchor we set,
      // and it doubles as the canvas-clamping margin.
      const MENU_HALF_HEIGHT = 20;
      // Gap between the pill's bottom edge and the element's top edge.
      const MENU_GAP = 8;
      // Horizontal half-extent budget, clamping only (see above).
      const MENU_HALF_WIDTH = 90;

      const recompute = () => {
        const rect = container.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          setMenuRect(null);
          return;
        }

        // (left, top) below describes the pill's CENTER anchor point,
        // not its top-left corner. `MenuPosition` styled-component uses
        // `transform: translate(-50%, -50%)` to self-center on this
        // anchor regardless of the pill's actual rendered width/height.
        let left: number;
        let top: number;

        // Try to project the selected element's source bounding box into
        // viewport coords. When this succeeds the menu floats above the
        // element center and tracks it as the user moves / animates the
        // composition. Falls back to canvas-center when we can't resolve
        // the source (e.g. element id not found in the top-level tree).
        const videoSource = videoSourceStore?.source as
          | Record<string, unknown>
          | undefined;
        const topLevel = videoSource?.elements as
          | ReadonlyArray<Record<string, unknown>>
          | undefined;
        const elementSource =
          activeTarget && Array.isArray(topLevel)
            ? findNodeInTree(topLevel, activeTarget.id)
            : null;
        const elementBox =
          elementSource && videoSource
            ? computeElementViewportBox(elementSource, videoSource, rect)
            : null;
        elementBoxRef.current = elementBox;

        // Pill center horizontally aligned with the element center, and
        // sitting fully ABOVE the element's top edge with a `MENU_GAP`
        // breathing gap — anchoring off the top edge (rather than a fixed
        // offset from the element's center) is what keeps the pill clear of
        // the element for tall selections, where a center-relative offset
        // lands the pill inside the box and covers the graphic.
        if (elementBox) {
          left = elementBox.left + elementBox.width / 2;
          top = elementBox.top - MENU_GAP - MENU_HALF_HEIGHT;
          // No room above (the element hugs the canvas top) — flip below the
          // element's bottom edge rather than let the clamp below push the
          // pill back on top of the element.
          if (top - MENU_HALF_HEIGHT < rect.top + MENU_GAP) {
            top =
              elementBox.top + elementBox.height + MENU_GAP + MENU_HALF_HEIGHT;
          }
        } else {
          left = rect.left + rect.width / 2;
          top = rect.top + MENU_GAP + MENU_HALF_HEIGHT;
        }

        // Clamp the CENTER anchor so the pill (after self-centering)
        // stays inside the canvas with an 8px safety gutter.
        const minLeft = rect.left + MENU_HALF_WIDTH + 8;
        const maxLeft = rect.right - MENU_HALF_WIDTH - 8;
        if (maxLeft > minLeft) {
          left = Math.min(Math.max(left, minLeft), maxLeft);
        }
        const minTop = rect.top + MENU_HALF_HEIGHT + 8;
        const maxTop = rect.bottom - MENU_HALF_HEIGHT - 8;
        if (maxTop > minTop) {
          top = Math.min(Math.max(top, minTop), maxTop);
        }

        setMenuRect(prev => {
          // Skip state updates when nothing changed so we don't thrash
          // React on every 10Hz tick.
          if (
            prev &&
            Math.abs(prev.left - left) < 0.5 &&
            Math.abs(prev.top - top) < 0.5
          ) {
            return prev;
          }
          return { left, top };
        });
      };
      recompute();

      // 10Hz poll for animation / drag tracking — see component-level
      // docstring for the rationale. 100ms is well below perceptible
      // toolbar lag while avoiding the 60Hz getBoundingClientRect +
      // layout flush cost of a perpetual rAF loop pinned active.
      const TICK_MS = 100;
      const intervalHandle = window.setInterval(recompute, TICK_MS);

      const resizeObserver = new ResizeObserver(recompute);
      resizeObserver.observe(container);
      window.addEventListener('scroll', recompute, true);
      window.addEventListener('resize', recompute);
      return () => {
        window.clearInterval(intervalHandle);
        resizeObserver.disconnect();
        window.removeEventListener('scroll', recompute, true);
        window.removeEventListener('resize', recompute);
      };
    }, [activeTarget, rendererContainerRef, videoSourceStore?.source]);

    // Close popover when the active selection clears OR moves to a
    // different composition.
    useEffect(() => {
      if (!activeTarget) {
        setPopoverAnchor(null);
      }
    }, [activeTarget]);

    // Fire analytics open event once per (target, fields) pair only when
    // the popover is actually open. Shared with `ClipTranscriptBridge`
    // via the hook so the two surfaces can't drift on signature shape
    // or `hasTranslation` derivation.
    useCompositionQuickEditOpenedTracking({
      isOpen: !!popoverAnchor,
      targetId: activeTarget?.id ?? null,
      compositionType: activeTarget?.compositionType ?? null,
      fields: popoverFields,
      storyId: storyId ?? null,
      surface,
    });

    const handleEdit = useCallback(
      (anchor: HTMLElement) => {
        // The pencil toggles: pressing it again while the popover is open
        // closes it (discarding an unsaved draft, exactly as Cancel does)
        // rather than re-anchoring the already-open popover. The pill stays —
        // only the popover comes and goes.
        if (popoverAnchor) {
          setPopoverAnchor(null);
          return;
        }
        // Snapshot the graphic's box as it is at open — the popover drops below
        // it instead of below the pencil, which sits inside it.
        setPopoverClearRect(elementBoxRef.current);
        setPopoverAnchor(anchor);
      },
      [popoverAnchor],
    );
    // PLAT-12102 — the text bar's Edit, where the surface wires one.
    useImperativeHandle(
      quickEditHandleRef,
      () => ({ openQuickEdit: handleEdit }),
      [handleEdit],
    );

    const handleDelete = useCallback(() => {
      if (!activeTarget || !editorStore || !adapter) return;
      try {
        // Shared delete-an-overlay sequence (command path + repaint + meta
        // cleanup + post-delete flush) — see `removeOverlayAndPersist`
        // (PLAT-10191). `onPersistAfterMutation` is the surface's flush hook
        // (Creator Studio wires it to `saveStoryAndVideo`; surfaces without
        // one persist elsewhere).
        const removed = removeOverlayAndPersist(activeTarget.id, {
          editor: editorStore.editor,
          video: adapter.currentVideo ?? null,
          refreshSource: () => videoSourceStore?.refreshSource(),
          persist: () => onPersistAfterMutation?.(),
          logger,
        });

        // Clear the renderer's selection so the menu drops out and a stray
        // active id doesn't keep a stale popover open.
        void adapter.setActiveElements();
        setLatchedTarget(null);
        setPopoverAnchor(null);
        logger.info('Timeline element removed via canvas action menu', {
          targetId: activeTarget.id,
          kind: activeTarget.kind,
          compositionType: activeTarget.compositionType,
          removed,
        });
      } catch (error) {
        logger.error('Timeline element removal failed', {
          targetId: activeTarget.id,
          ...extractErrorInfo(error),
        });
      }
    }, [
      activeTarget,
      editorStore,
      adapter,
      videoSourceStore,
      onPersistAfterMutation,
    ]);

    const handlePopoverClose = useCallback(() => {
      setPopoverAnchor(null);
    }, []);

    // PLAT-10046 — wrap the store's retranslate method so the popover
    // doesn't need to know about videoSourceStore / extraElementData /
    // language. The closure extracts the live `compositionTargets` on
    // every call so a mid-popover canvas edit (rare) still picks up
    // the freshest source.
    const retranslateCompositionDispatch: RetranslateCompositionDispatch =
      useCallback(
        async ({ force, compositionElementId, sourceTexts }) => {
          // Canonical (non-windowed) elements from the editor, not the windowed
          // preview source — see `getTranslatableSourceElements`.
          const { compositionTargets } = extractTranslatableElements({
            videoSourceElements: getTranslatableSourceElements(
              editorStore?.editor,
            ),
            extraElementData,
            targetLanguage: activeLanguage,
          });
          const result = await translationStore.retranslateCompositionPhrases({
            lang: activeLanguage,
            compositionTargets,
            force,
            // PLAT-10875: the active workspace's showcase, so the backend
            // honors this workspace's TM even if the story is in several.
            showcaseId: adapter?.organization?.id,
            ...routeRetranslateTargetId(
              compositionElementId,
              compositionTargets,
            ),
            ...(sourceTexts ? { sourceTexts } : {}),
          });
          // PLAT-10046 — explicit refresh after the cache swap, mirroring
          // the upsert path. See `ClipTranscriptBridge` for rationale.
          videoSourceStore?.refreshSource();
          return result;
        },
        [
          editorStore,
          videoSourceStore,
          extraElementData,
          activeLanguage,
          translationStore,
          adapter,
        ],
      );

    // PLAT-10046 — pin upsert + revert dispatchers. Shared with the
    // transcript bridge via `useCompositionPinDispatchers`.
    const refreshCanvasSource = useCallback(() => {
      videoSourceStore?.refreshSource();
    }, [videoSourceStore]);
    const {
      upsert: upsertCompositionPhraseDispatch,
      revert: removeCompositionPhrasePinDispatch,
    } = useCompositionPinDispatchers({
      compositionElementId: activeTarget?.id ?? null,
      compositionPhrases,
      lang: activeLanguage,
      translationStore,
      editorDisplayName: userIdentity.currentEditor,
      onAfterCommit: refreshCanvasSource,
    });

    const handleSaved = useCallback(
      (summary: {
        originalRowsCommitted: number;
        translatedRowsCommitted: number;
      }) => {
        if (!activeTarget) return;
        if (summary.originalRowsCommitted === 0) return;
        // PLAT-10066 — emit active caption language alongside the event
        // so dashboards can distinguish original-language edits from
        // non-translatable / quick-edit-row edits under a translated
        // session. Matches the bridge's `handleSaved` shape.
        const language = isOriginalLang(activeLanguage)
          ? '<original>'
          : activeLanguage;
        for (const f of popoverFields) {
          trackCompositionTextEdited({
            story_id: storyId ?? undefined,
            composition_element_id: activeTarget.id,
            composition_type: activeTarget.compositionType,
            surface,
            language,
            source_text_length: f.originalValue.length,
          });
        }
      },
      [activeTarget, popoverFields, storyId, activeLanguage],
    );

    if (typeof document === 'undefined') return null;

    // Render the menu whenever an active target resolves — even when no
    // editable fields are found (the menu's Edit button is then disabled
    // with a tooltip). This makes "menu missing" reports easy to triage:
    // if no menu appears at all, the selection wiring is the problem; if
    // the menu appears but Edit is disabled, the composition template
    // has no `allowQuickEdit: true` controls.
    // A text with a bar (`pillSuppressed`) has a target for the popover only.
    const menuVisible = !!activeTarget && !!menuRect && !pillSuppressed;

    return ReactDOM.createPortal(
      <OverlayLayer aria-hidden={false}>
        {menuVisible && activeTarget && menuRect && (
          <MenuPosition
            style={{
              left: `${menuRect.left}px`,
              top: `${menuRect.top}px`,
            }}
          >
            <CompositionCanvasActionMenu
              ariaLabel={activeTarget.ariaLabel}
              editTooltip={`Edit ${activeTarget.headerLabel}`}
              // PLAT-10973 — the click that opens the popover leaves the cursor
              // on the pencil, so its hover tooltip would sit on top of the
              // popover it just opened, labelling the same graphic twice.
              editTooltipSuppressed={!!popoverAnchor}
              onEdit={handleEdit}
              onDelete={handleDelete}
              editDisabled={popoverFields.length === 0}
              editDisabledReason={
                activeTarget.kind === 'composition'
                  ? 'No editable text fields on this composition'
                  : 'This overlay has no editable text'
              }
            />
          </MenuPosition>
        )}
        {popoverAnchor &&
          activeTarget &&
          editorStore &&
          popoverFields.length > 0 && (
            <CompositionQuickEditPopover
              // Remount per graphic — selecting another composition on the
              // canvas swaps `activeTarget` without unmounting, and the AI
              // text control seeds its base-text scope and instructions at
              // mount (rows are keyed by control id, which repeats across
              // cards).
              key={activeTarget.id}
              // Composition fields walk `elementPath` from the active
              // placement id; a synthesized standalone/nested text field
              // (empty path) writes to its own resolved leaf id. See
              // `resolveQuickEditTargetId`.
              targetId={resolveQuickEditTargetId(
                popoverFields,
                activeTarget.id,
              )}
              headerLabel={activeTarget.headerLabel}
              // PLAT-10066 — surface the active caption language top-right in
              // the header (next to the close button) under non-original mode
              // so the user is never confused about which language pin they're
              // editing. `null` under original language hides it.
              activeLanguageLabel={
                isOriginalLang(activeLanguage)
                  ? null
                  : formatLanguageCaption(activeLanguage)
              }
              anchorElement={popoverAnchor}
              // PLAT-11090 — open below the graphic rather than below the
              // pencil, which the menu parks inside the graphic's box and so
              // put the popover across the overlay being edited.
              clearRect={popoverClearRect}
              fields={popoverFields}
              // The clip preview's editor store satisfies `EditorStoreLike`
              // (it exposes the same `editor.commandUpdateElement` /
              // `editor.timeline.getElement` surface the Creator Studio
              // text controls use). Saves go through the editor's command
              // system so the renderer paints; bypassing the editor via
              // direct `setSource` writes (the previous shape) left the
              // timeline model and renderer out of sync.
              editorStore={editorStore}
              // PLAT-10046 — translated-row + footer-action support shared
              // with the transcript surface via the bridge. See
              // `ClipTranscriptBridge` for the symmetric wiring.
              activeLanguage={activeLanguage}
              compositionPhrases={compositionPhrases}
              retranslateCompositionPhrases={retranslateCompositionDispatch}
              upsertCompositionPhraseTranslation={
                upsertCompositionPhraseDispatch
              }
              removeCompositionPhrasePin={removeCompositionPhrasePinDispatch}
              sourceLanguage={transcriptionStore.language}
              // PLAT-10046 analytics context — symmetric with the
              // transcript surface so funnel events from both entry paths
              // line up on `composition_translate_clicked` /
              // `composition_retranslate_clicked` /
              // `composition_translate_failed`.
              storyId={storyId ?? null}
              compositionType={activeTarget.compositionType}
              surface={surface}
              aiTextContext={aiTextPopoverContext}
              onSaved={handleSaved}
              onClose={handlePopoverClose}
            />
          )}
      </OverlayLayer>,
      document.body,
    );
  },
);

const OverlayLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: ${CLIP_PREVIEW_Z.canvasActionLayer};
`;

const MenuPosition = styled.div`
  position: fixed;
  pointer-events: none;
  /* (left, top) from inline style is the desired CENTER of the pill.
     translate(-50%, -50%) self-centers the pill on that anchor regardless
     of the pill's actual rendered width/height, so we don't depend on a
     hard-coded MENU_EST_WIDTH/HEIGHT estimate to align it. */
  transform: translate(-50%, -50%);

  /* Re-enable pointer events on the menu's own pill (the menu itself
     sets pointer-events: auto on its surface) so clicks hit the
     buttons but pass through the wrapper. */
  & > * {
    pointer-events: auto;
  }
`;
